// ============================================
// File: src/components/teams/hooks/useMembershipGate.js
// Ensures user is an admin or has a members/{uid} doc
// ============================================
import { useEffect, useState } from 'react';
import { doc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { db } from '../../../firebase-client';
import { useNavigate } from 'react-router-dom';

const MEMBER_DOC_WAIT_MS = 15000;

export function useMembershipGate({
  id,
  user,
  authLoading,
  team
}) {
  const navigate = useNavigate();
  const [authzChecked, setAuthzChecked] = useState(false);
  const userId = user?.uid || '';
  const teamReady = Boolean(team);
  const adminIdsKey = Array.isArray(team?.admins) ? team.admins.join('|') : '';
  const permissionKeysKey = Array.isArray(team?.myAdminAssignment?.permissionKeys)
    ? team.myAdminAssignment.permissionKeys.join('|')
    : '';

  useEffect(() => {
    let didCancel = false;
    if (authLoading || !userId || !id || !teamReady) {
      setAuthzChecked(false);
      return () => {
        didCancel = true;
      };
    }
    const isAdmin = adminIdsKey.split('|').filter(Boolean).includes(userId);
    const hasAssignedAdminPermissions = permissionKeysKey.split('|').filter(Boolean).length > 0;
    if (team?.billingAccess?.locked === true) {
      if (isAdmin || permissionKeysKey.split('|').includes('finance.manage')) {
        navigate(`/teams/${id}/club-billing`, { replace: true });
      } else {
        navigate('/teams', { replace: true });
      }
      return () => { didCancel = true; };
    }
    if (isAdmin || hasAssignedAdminPermissions) {
      setAuthzChecked(true);
      return () => {
        didCancel = true;
      };
    }

    setAuthzChecked(false);
    const memberRef = doc(db, 'teams', id, 'members', userId);
    const timeoutId = setTimeout(() => {
      if (!didCancel) {
        navigate('/teams', { replace: true });
      }
    }, MEMBER_DOC_WAIT_MS);
    const unsubscribe = safeOnSnapshot(
      memberRef,
      (snap) => {
        if (didCancel) return;
        if (snap.exists()) {
          clearTimeout(timeoutId);
          setAuthzChecked(true);
        }
      },
      (err) => {
        if (!didCancel) {
          clearTimeout(timeoutId);
          navigate('/teams', { replace: true });
        }
      }
    );
    return () => {
      didCancel = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [adminIdsKey, authLoading, id, navigate, permissionKeysKey, team?.billingAccess?.locked, teamReady, userId]);
  return {
    authzChecked
  };
}
